import React, { useCallback, useRef } from 'react';
import { Params, useLocation } from 'react-router-dom';
import ScrollToContext, { StepsContextProps } from './ScrollTo.context';
import useEffectSkipFirst from '../../hooks/useEffectSkipFirst';
import useQueryParams from 'hooks/useQueryParams';
import { scrollToTop } from 'utils';

type ScrollToProps = {
  children: React.ReactNode;
};

export type StepsRef = StepsContextProps;

const ScrollTo: React.FC<ScrollToProps> = (props) => {
  const { children } = props;

  const container = useRef<HTMLElement>();
  const shouldScroll = useRef<boolean>(true);
  const shouldScrollOnParams = useRef<boolean>(true);

  const { pathname, hash } = useLocation();

  const { params } = useQueryParams<Params>();

  const handleScrollToTop = useCallback(() => {
    if (container.current) container.current.scrollIntoView({ block: 'start' });
    else scrollToTop();
  }, []);

  useEffectSkipFirst(() => {
    if (hash || !shouldScroll.current) return;
    handleScrollToTop();
  }, [pathname]);

  useEffectSkipFirst(() => {
    if (hash || !shouldScroll.current || !shouldScrollOnParams.current) return;
    handleScrollToTop();
  }, [params]);

  const setContainer = useCallback((element: HTMLElement) => {
    container.current = element;
  }, []);

  const resetContainer = useCallback(() => (container.current = null), []);

  const setShouldScroll = useCallback(
    (value: boolean) => (shouldScroll.current = value),
    [],
  );

  const setShouldScrollOnParams = useCallback(
    (value: boolean) => (shouldScrollOnParams.current = value),
    [],
  );

  return (
    <ScrollToContext.Provider
      value={{
        container,
        setContainer,
        setShouldScroll,
        setShouldScrollOnParams,
        resetContainer,
      }}
    >
      {children}
    </ScrollToContext.Provider>
  );
};

export default ScrollTo;
